import moment from 'moment';

/**
 * Determines whether a subscription has expired.
 *
 * Only the calendar date of `expires_at` is significant — the time component is
 * deliberately ignored. This mirrors the API, which treats a subscription as
 * expired once its expiry date has been reached, regardless of the time of day.
 * Comparing the full datetime (as the Portal previously did) caused a
 * subscription that had expired on the API side to still appear active in the
 * Portal until the stored time of day had also passed.
 *
 * A missing `expires_at` is treated as "not expired" (no expiry date), matching
 * the previous behaviour where an invalid/absent date never compared as before.
 */
export function isSubscriptionExpired(expiresAt: string | Date | null | undefined): boolean {
  if (!expiresAt) {
    return false;
  }
  return moment(expiresAt).isSameOrBefore(moment(), 'day');
}

/**
 * Determines whether a subscription order's "activate by" deadline has passed.
 *
 * As with `expires_at`, only the calendar date of `activate_by` is significant —
 * the time component is deliberately ignored, mirroring the API. The deadline is
 * treated as passed only once the current date is strictly after the activate-by
 * date, so an order can still be activated throughout the activate-by date itself.
 *
 * A missing `activate_by` is treated as "not passed", matching the previous
 * behaviour where an invalid date never compared as before the current time.
 */
export function isActivateByPassed(activateBy: string | Date | null | undefined): boolean {
  if (!activateBy) {
    return false;
  }
  return moment(activateBy).isBefore(moment(), 'day');
}
